import React from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import PetsIcon from "@mui/icons-material/Pets";
import PersonIcon from "@mui/icons-material/Person";
import CreditCardIcon from "@mui/icons-material/CreditCard";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router";
import useMediaQuery from "@mui/material/useMediaQuery";

import { PetProfile, UserProfile } from "../../types";
import axiosInstance from "../../utils/axiosInstance";
import { translate } from "../../utils/localisation";

/** ---------------- Utils ---------------- */

const CLICK_INDICATORS = ["pack_subscription_id"] as const;

const USER_TABLE_FIELD_ORDER = [
  "email",
  "first_name",
  "last_name",
  "multiple_dog_owner",
  "locale",
  "kinship_user_id",
  "is_active",
  "iterable_link",
  "linnaeus_linked",
  "has_linnaeus_division",
  "image_asset_id",
  "created_at",
  "updated_at",
  "source",
  "id",
  "sub",
  "kin_subscription_id",
] as const;

const USER_CLICKABLE_KEYS = ["iterable_link"];

const PET_TABLE_FIELD_ORDER = [
  "name",
  "animal_type",
  "gender",
  "breed",
  "spayed_or_neutered",
  "weight",
  "microchip_id",
  "birth_date",
  "acquisition_date",
  "is_active",
  "created_at",
  "updated_at",
  "kinship_pet_id",
  "id",
  "created_by_user_profile_id",
  "updated_by_user_profile_id",
  "linnaeus_linked",
  "linnaeus_linked_pet_name",
  "linnaeus_preferred_practice",
] as const;

const PET_HIDDEN_KEYS = new Set([
  "ownerships",
  "petFoods",
  "customFields",
  "bookingLocations",
  "petFoods", // yes twice is fine but harmless
]);

const toStartCase = (k: string) =>
  k
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());

function safeStr(v: unknown) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function isIsoDate(v: unknown) {
  if (typeof v !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}T/.test(v) || /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function formatDateShort(v: string) {
  return v.slice(0, 10);
}

function formatBool(v: unknown) {
  if (v === true) return translate("common.yes") || "Yes";
  if (v === false) return translate("common.no") || "No";
  return safeStr(v);
}

function precisionLabel(v: unknown) {
  const raw = safeStr(v).toUpperCase();
  if (!raw) return "";
  if (raw === "DAY") return translate("common.precision.day") || "Day";
  if (raw === "MONTH") return translate("common.precision.month") || "Month";
  if (raw === "YEAR") return translate("common.precision.year") || "Year";
  return raw;
}

function formatArrayCount(arr: unknown[]) {
  const n = arr.length;
  const item = translate("common.item") || "item";
  const items = translate("common.items") || "items";
  return n === 1 ? `1 ${item}` : `${n} ${items}`;
}

function extractEntitlementMessage(e: any) {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message;
  if (status === 403 && typeof msg === "string") {
    const lower = msg.toLowerCase();
    if (lower.includes("entitlement denied")) return msg;
  }
  return "";
}

function getFriendlyError(e: any) {
  const entitlement = extractEntitlementMessage(e);
  if (entitlement) return entitlement;

  const data = e?.response?.data;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;

  return safeStr(e?.message || "Something went wrong.");
}

/** ---------------- KV Card (smart rendering) ---------------- */

type KVCardProps = {
  title: React.ReactNode;
  data?: Record<string, unknown> | null;
  loading?: boolean;
  headerAction?: React.ReactNode;
  clickableKeys?: string[];
  hiddenKeys?: Set<string>;
  onClick?: (
    event: React.MouseEvent<unknown>,
    key: string,
    value: string,
  ) => void;
};

function KVCard({
  title,
  data,
  loading = false,
  headerAction,
  clickableKeys = [],
  hiddenKeys,
  onClick,
}: KVCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const entries = useMemo(() => {
    if (!data) return [];

    return (
      Object.entries(data)
        .filter(([_k, v]) => v !== undefined && v !== null && v !== "")
        // hide precision keys; we render precision inline with its date field
        .filter(([k]) => !k.endsWith("_precision"))
        .filter(([k]) => !hiddenKeys?.has(k))
    );
  }, [data, hiddenKeys]);

  const renderValue = (key: string, raw: unknown) => {
    // Date + precision merge: acquisition_date + acquisition_date_precision
    if (key === "acquisition_date" && typeof raw === "string") {
      const p = precisionLabel((data as any)?.acquisition_date_precision);
      return p ? `${formatDateShort(raw)}` : formatDateShort(raw);
    }

    // Date + precision merge: birth_date + birth_date_precision
    if (key === "birth_date" && typeof raw === "string") {
      const p = precisionLabel((data as any)?.birth_date_precision);
      return p ? `${formatDateShort(raw)}` : formatDateShort(raw);
    }

    if (typeof raw === "boolean") return formatBool(raw);
    if (isIsoDate(raw)) return formatDateShort(String(raw));

    if (Array.isArray(raw)) return formatArrayCount(raw);

    // avoid [object Object]
    if (typeof raw === "object" && raw) {
      return translate("common.view_details") || "View details";
    }

    return safeStr(raw);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        borderColor: alpha(theme.palette.divider, 0.9),
        boxShadow: `0 10px 26px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <CardHeader
        title={title}
        titleTypographyProps={{ fontWeight: 900 }}
        action={headerAction}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.035),
          borderBottom: "1px solid",
          borderColor: "divider",
          py: 1.5,
        }}
      />

      <CardContent sx={{ p: 2 }}>
        {loading ? (
          <Stack spacing={1}>
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} height={22} />
            ))}
          </Stack>
        ) : entries.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {translate("common.no_data") ||
              translate("no_data") ||
              "No data available."}
          </Typography>
        ) : (
          <Stack spacing={isMobile ? 1 : 0.75}>
            {entries.map(([key, raw]) => {
              const value = renderValue(key, raw);
              const isClickable =
                clickableKeys.includes(key) &&
                typeof raw === "string" &&
                raw.trim().length > 0;

              return (
                <Box
                  key={key}
                  onClick={
                    isClickable && onClick
                      ? e => onClick(e, key, String(raw))
                      : undefined
                  }
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    py: 0.75,
                    px: 1,
                    borderRadius: 2,
                    cursor: isClickable ? "pointer" : "default",
                    "&:hover": isClickable
                      ? { bgcolor: alpha(theme.palette.primary.main, 0.06) }
                      : undefined,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 800 }}
                    >
                      {translate(`user-profile.fields.${key}`) ||
                        translate(`pet.fields.${key}`) ||
                        toStartCase(key)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {value || translate("common.none") || "—"}
                    </Typography>
                  </Box>

                  {isClickable ? (
                    <Tooltip
                      title={
                        translate("common.open_in_new") || "Open in new tab"
                      }
                    >
                      <IconButton size="small" sx={{ mt: 0.25 }}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/** ---------------- Specialized sections ---------------- */

type Ownership = { ownerKinshipId?: string | null; ownerEmail?: string | null };

function OwnershipsTable({ ownerships }: { ownerships: Ownership[] }) {
  const theme = useTheme();

  if (!ownerships.length) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {translate("pet.ownerships.empty") ||
          translate("common.no_data") ||
          "No data available."}
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 3, overflow: "hidden" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
            <TableCell sx={{ fontWeight: 900 }}>
              {translate("pet.ownerships.owner_id") || "Owner ID"}
            </TableCell>
            <TableCell sx={{ fontWeight: 900 }}>
              {translate("pet.ownerships.owner_email") || "Owner Email"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ownerships.map((o, idx) => (
            <TableRow key={idx} hover>
              <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                {o.ownerKinshipId || translate("common.none") || "—"}
              </TableCell>
              <TableCell>
                {o.ownerEmail || translate("common.none") || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type PetFoods = { branded?: any[]; custom?: any[] };

function PetFoodsSummary({ petFoods }: { petFoods: PetFoods | null }) {
  const brandedCount = Array.isArray(petFoods?.branded)
    ? petFoods!.branded!.length
    : 0;
  const customCount = Array.isArray(petFoods?.custom)
    ? petFoods!.custom!.length
    : 0;

  if (!petFoods || (brandedCount === 0 && customCount === 0)) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {translate("pet.foods.empty") ||
          translate("common.no_data") ||
          "No data available."}
      </Typography>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{ flexWrap: "wrap" }}
    >
      <Chip
        label={`${translate("pet.foods.branded") || "Branded"}: ${brandedCount}`}
        variant="outlined"
        sx={{ fontWeight: 900 }}
      />
      <Chip
        label={`${translate("pet.foods.custom") || "Custom"}: ${customCount}`}
        variant="outlined"
        sx={{ fontWeight: 900 }}
      />
    </Stack>
  );
}

/** ---------------- Page Component ---------------- */

export function UserProfileView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const { userId } = useParams();

  const [pets, setPets] = useState<PetProfile[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [subData, setSubData] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [confirmPet, setConfirmPet] = useState<PetProfile | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function fetchExtraDetails() {
      try {
        setLoading(true);
        setError("");

        const res = await axiosInstance.get(`/admin/user-profile/${userId}`);
        if (!mounted) return;
        console.log(res, "got user profile details");
        setPets(res.data.details?.pets ?? []);
        setSubData(res.data.details?.stripe_customer_info?.[0] ?? null);
        setUserProfile(res.data.details.userDetails);
      } catch (e: any) {
        if (!mounted) return;
        setError(getFriendlyError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (userId) fetchExtraDetails();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const fullName =
    `${userProfile?.first_name || ""} ${userProfile?.last_name || ""}`.trim();

  const activePets = useMemo(() => pets.filter(p => p.is_active), [pets]);

  const sortedPets = useMemo(() => {
    const list = pets.slice();
    list.sort((a, b) => {
      const aa = a.is_active ? 0 : 1;
      const bb = b.is_active ? 0 : 1;
      if (aa !== bb) return aa - bb;
      return safeStr(a.name).localeCompare(safeStr(b.name));
    });
    return list;
  }, [pets]);

  const stripeSubscriptionId = safeStr(subData?.pack_subscription_id);

  const userTableData = useMemo(() => {
    const up = userProfile as Record<string, unknown> | null | undefined;
    const sd = subData as Record<string, unknown> | null | undefined;
    if (!up) return null;
    const email = typeof up.email === "string" ? up.email : "";
    const iterableLinkDisplay = email
      ? `https://app.iterable.com/users/profiles/${email}`
      : "";
    const kinSubId =
      up.kin_subscription_id != null && up.kin_subscription_id !== ""
        ? up.kin_subscription_id
        : sd?.pack_subscription_id ?? sd?.kin_subscription_id;
    const result: Record<string, unknown> = {};
    for (const key of USER_TABLE_FIELD_ORDER) {
      if (key === "iterable_link") {
        result[key] = iterableLinkDisplay;
      } else if (
        key === "linnaeus_linked" ||
        key === "has_linnaeus_division"
      ) {
        result[key] = up[key] === true;
      } else if (key === "kin_subscription_id") {
        result[key] = kinSubId;
      } else {
        result[key] = up[key];
      }
    }
    return result;
  }, [userProfile, subData]);

  const openStripe = () => {
    if (!stripeSubscriptionId) return;
    window.open(
      `https://dashboard.stripe.com/test/subscriptions/${stripeSubscriptionId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleKVClick = (
    _event: React.MouseEvent<unknown>,
    key: string,
    value: string,
  ) => {
    if (key === "pack_subscription_id") {
      window.open(
        `https://dashboard.stripe.com/test/subscriptions/${value}`,
        "_blank",
        "noopener,noreferrer",
      );
    }
    if (key === "iterable_link") {
      if (!value) return;
      window.open(value, "_blank", "noopener,noreferrer");
    }
  };

  const requestDelete = (pet: PetProfile) => setConfirmPet(pet);

  const handleDelete = async () => {
    if (!confirmPet) return;
    try {
      await axiosInstance.delete(`/admin/pet-profile/${confirmPet.id}`);
      setPets(list =>
        list.map(p =>
          p.id === confirmPet.id ? { ...p, is_active: false } : p,
        ),
      );
    } catch (e: any) {
      setError(getFriendlyError(e));
    } finally {
      setConfirmPet(null);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1.5, sm: 2.5, md: 3 },
        pb: 6,
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: { xs: 2, sm: 2.5, md: 3 },
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          background: "#fff",
          boxShadow: `0 10px 26px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight={950}
                sx={{ lineHeight: 1.1 }}
              >
                {translate("user-profile.view.title") ||
                  translate("user-profiles-selected-screen.title-text") ||
                  "User Profile"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {fullName ? fullName : translate("common.none") || "—"}
                {userProfile?.email ? ` • ${userProfile.email}` : ""}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <Button variant="outlined" onClick={() => navigate(-1)}>
                {translate("common.back") || translate("back") || "Back"}
              </Button>

              {stripeSubscriptionId ? (
                <Button
                  variant="contained"
                  onClick={openStripe}
                  startIcon={<OpenInNewIcon />}
                  sx={{
                    background: "#0EA5E9",
                    "&:hover": { background: "#0284C7" },
                  }}
                >
                  {translate("user-profile.actions.open_stripe") ||
                    translate("open-stripe") ||
                    "Open Stripe"}
                </Button>
              ) : null}
            </Stack>
          </Stack>

          <Divider />

          {/* Quick stats */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              icon={<PersonIcon />}
              label={`${translate("user-profile.stats.user_id") || "User ID"}: ${userId || translate("common.none") || "—"}`}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              icon={<PetsIcon />}
              label={`${translate("user-profile.stats.pets_total") || "Pets"}: ${pets.length}`}
              sx={{
                fontWeight: 900,
                bgcolor: alpha("#10B981", 0.12),
                border: `1px solid ${alpha("#10B981", 0.25)}`,
              }}
            />
            <Chip
              label={`${translate("user-profile.stats.pets_active") || "Active pets"}: ${activePets.length}`}
              sx={{
                fontWeight: 900,
                bgcolor: alpha("#0EA5E9", 0.1),
                border: `1px solid ${alpha("#0EA5E9", 0.22)}`,
              }}
            />
            <Chip
              icon={<CreditCardIcon />}
              label={`${translate("user-profile.stats.subscription") || "Subscription"}: ${
                stripeSubscriptionId
                  ? translate("user-profile.stats.subscription_yes") || "Yes"
                  : translate("user-profile.stats.subscription_no") || "No"
              }`}
              sx={{
                fontWeight: 900,
                bgcolor: stripeSubscriptionId
                  ? alpha("#F59E0B", 0.12)
                  : alpha("#6B7280", 0.12),
                border: `1px solid ${
                  stripeSubscriptionId
                    ? alpha("#F59E0B", 0.25)
                    : alpha("#6B7280", 0.22)
                }`,
              }}
            />

            {error ? (
              <Alert severity="error" sx={{ width: "100%", mt: 1 }}>
                {error}
              </Alert>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      {/* Main grid */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <KVCard
            title={translate("user-profile.sections.user") || "User"}
            data={userTableData as any}
            loading={loading}
            clickableKeys={USER_CLICKABLE_KEYS}
            onClick={handleKVClick}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <KVCard
            title={
              translate("user-profile.sections.subscription") || "Subscription"
            }
            data={subData as any}
            loading={loading}
            clickableKeys={CLICK_INDICATORS as unknown as string[]}
            onClick={handleKVClick}
            headerAction={
              stripeSubscriptionId ? (
                <Tooltip
                  title={translate("common.open_in_new") || "Open in new tab"}
                >
                  <IconButton size="small" onClick={openStripe}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          />
        </Grid>

        {/* Pets */}
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6" fontWeight={950}>
              {translate("user-profile.sections.pets") ||
                translate("pets_title") ||
                "Pets"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {!loading
                ? `${pets.length} ${translate("common.total") || "total"}`
                : ""}
            </Typography>
          </Stack>
        </Grid>

        {!loading && sortedPets.length === 0 ? (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography fontWeight={900} sx={{ mb: 0.25 }}>
                  {translate("pets.empty.title") ||
                    translate("no-pets-found") ||
                    "No pets found"}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {translate("pets.empty.subtitle") ||
                    translate("no-pets-found-desc") ||
                    "This user doesn’t have any pets attached to their profile."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          sortedPets.map(pet => {
            const petAny: any = pet as any;
            const ownerships: Ownership[] = Array.isArray(petAny.ownerships)
              ? petAny.ownerships
              : [];
            const petFoods: PetFoods | null = petAny.petFoods
              ? petAny.petFoods
              : null;
            const petDisplay: Record<string, unknown> = {};

            PET_TABLE_FIELD_ORDER.forEach(key => {
              if (Array.isArray(PET_HIDDEN_KEYS) && PET_HIDDEN_KEYS.includes(key)) {
                return;
              }
              if (key === "weight") {
                const weightValue = petAny.weight;
                if (weightValue != null && weightValue !== "") {
                  petDisplay[key] = `${weightValue} kg`;
                }
                return;
              }

              if (key === "linnaeus_linked") {
                petDisplay[key] = petAny.linnaeus_linked === true;
                return;
              }

              if (key === "linnaeus_linked_pet_name") {
                const v = petAny[key];
                if (v != null && v !== "") {
                  petDisplay[key] = v;
                }
                return;
              }

              if (key === "linnaeus_preferred_practice") {
                const v = petAny[key];
                petDisplay[key] =
                  v != null && v !== ""
                    ? v
                    : translate("common.none") || "—";
                return;
              }

              const v = petAny[key];
              if (v !== undefined && v !== null && v !== "") {
                petDisplay[key] = v;
              }
            });

            return (
              <Grid item xs={12} md={6} key={pet.id}>
                <Stack spacing={1.25}>
                  <KVCard
                    title={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        <Typography
                          fontWeight={950}
                          sx={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {translate([
                            "user-profiles-selected-screen.pet-table-caption-text",
                            { var: pet?.name || "Pet" },
                          ]) ||
                            `${translate("user-profile.pet.title") || "Pet"}: ${pet?.name || ""}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={
                            pet.is_active
                              ? translate("active") || "Active"
                              : translate("inactive") || "Inactive"
                          }
                          sx={{
                            fontWeight: 900,
                            borderRadius: 999,
                            bgcolor: pet.is_active
                              ? alpha("#10B981", 0.16)
                              : alpha("#6B7280", 0.16),
                            border: `1px solid ${
                              pet.is_active
                                ? alpha("#10B981", 0.3)
                                : alpha("#6B7280", 0.25)
                            }`,
                          }}
                        />
                      </Stack>
                    }
                    data={petDisplay}
                    loading={loading}
                    hiddenKeys={undefined}
                    headerAction={
                      pet.is_active ? (
                        <Tooltip
                          title={translate("deactivate") || "Deactivate"}
                        >
                          <IconButton
                            size="small"
                            onClick={() => requestDelete(pet)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />

                  <Card variant="outlined" sx={{ borderRadius: 4 }}>
                    <CardHeader
                      title={
                        translate("pet.sections.ownerships") || "Ownerships"
                      }
                      titleTypographyProps={{ fontWeight: 900 }}
                      sx={{
                        py: 1.25,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <CardContent sx={{ pt: 1.5 }}>
                      {loading ? (
                        <Stack spacing={1}>
                          {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} height={22} />
                          ))}
                        </Stack>
                      ) : (
                        <OwnershipsTable ownerships={ownerships} />
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined" sx={{ borderRadius: 4 }}>
                    <CardHeader
                      title={translate("pet.sections.foods") || "Foods"}
                      titleTypographyProps={{ fontWeight: 900 }}
                      sx={{
                        py: 1.25,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <CardContent sx={{ pt: 1.5 }}>
                      {loading ? (
                        <Stack spacing={1}>
                          {[...Array(2)].map((_, i) => (
                            <Skeleton key={i} height={22} />
                          ))}
                        </Stack>
                      ) : (
                        <PetFoodsSummary petFoods={petFoods} />
                      )}
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Confirm dialog for pet deactivation */}
      <Dialog open={!!confirmPet} onClose={() => setConfirmPet(null)}>
        <DialogTitle>
          {translate("pets.deactivate.confirm_title") ||
            translate("confirm-deactivation") ||
            "Confirm deactivation"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {translate([
              "pets.deactivate.confirm_body",
              { name: confirmPet?.name || "" },
            ]) ||
              translate("are-you-sure-deactivate") ||
              `Are you sure you want to deactivate ${confirmPet?.name}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmPet(null)}>
            {translate("cancel") || "Cancel"}
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {translate("deactivate") || "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
